
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { DevolutionStateEnums } from 'src/app/enum/devolution-state-enums';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { DevolutionChartModel } from 'src/app/models/charts/rental/devolution-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DevolutionDetailTableModel } from 'src/app/models/tables/rental/devolution-detail-table.model';
import { DevolutionTableModel } from 'src/app/models/tables/rental/devolution-table.model';
import { InvoiceDevolutionTableModel } from 'src/app/models/tables/rental/invoice-devolution-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { DevolutionDetailService } from 'src/app/services/rental/devolution-detail.service';
import { DevolutionService } from 'src/app/services/rental/devolution.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

const DevolutionIndicatorsConstants = {
  states: [
    { id: DevolutionStateEnums.abierto, label: 'Abiertas', color: '#EF4444' },
    { id: DevolutionStateEnums.impreso, label: 'Impresas', color: '#3B82F6' },
    { id: DevolutionStateEnums.cerrado, label: 'Cerradas', color: '#198754' },
    { id: DevolutionStateEnums.anulado, label: 'Anuladas', color: '#FB923C' }
  ]
};

@Component({
  selector: 'app-devolution',
  templateUrl: './devolution.component.html',
  styleUrls: ['./devolution.component.scss'],
  standalone: false
})
export class DevolutionComponent implements OnInit, OnDestroy {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<DevolutionDetailTableModel> = new PagedResponseModel<DevolutionDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<DevolutionTableModel> = new PagedResponseModel<DevolutionTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new DevolutionChartModel());
  indicatorsRequestId: number = 0;
  invoiceData: PagedResponseModel<InvoiceDevolutionTableModel> = new PagedResponseModel<InvoiceDevolutionTableModel>();
  invoiceHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  loadingIndicators: boolean = false;
  nameWindow: string = 'Devoluciones';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = "";
  userAuthenticated: UserModel = new UserModel();

  constructor(private devolutionDetailService: DevolutionDetailService,
    private devolutionService: DevolutionService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService,
    private headerService: HeaderService) {
    this.getPermission();
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('devolution');
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getDevolutionsWithPagination(),
      this.getIndicatorsForGeneralConsultation()
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  buildIndicatorCharts(indicators: DevolutionChartModel): IndicatorChart[] {
    return [
      this.buildStatesChart(
        'Devoluciones por estado', indicators.devolutionStates, indicators.devolutionTotal
      ),
      this.buildStatesChart(
        'Predevoluciones por estado', indicators.predevolutionStates, indicators.predevolutionTotal
      )
    ];
  }

  buildStatesChart(title: string, items: ItemChartModel[], total: number): IndicatorChart {
    const { states } = DevolutionIndicatorsConstants;
    return {
      type: IndicatorChartTypeEnums.donut,
      title,
      labels: states.map(state => state.label),
      values: states.map(state => this.getIndicatorQuantity(items, state.id)),
      colors: states.map(state => state.color),
      total
    };
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.router.navigate(['/devolution/register']);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear devoluciones. Comuníquese con el administrador del sistema.'
      );
    }
  }

  async exportDetailsConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.devolutionDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'DevolucionesDetalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el detalle de las devoluciones.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar detalle de devoluciones, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportGeneralConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await firstValueFrom(this.devolutionService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant, timeZoneId));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Devoluciones.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error to export devolutions.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar devoluciones, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportInvoiceForDevolutionConsultation() {
    try {
      const blob = await firstValueFrom(this.devolutionService.exportInvoiceForDevolutionConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Devolución Facturas.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las facturas de devoluciones detalle.', error);
    }
  }

  async getAllInvoiceForDevolutionsWithPagination() {
    try {
      this.invoiceData = await firstValueFrom(this.devolutionService.getAllInvoiceForDevolutionsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar las facturas para las devoluciones.', error);
    }
  }

  async getColumnsForDetailsConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.devolutionDetailService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error to search columns for query.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.devolutionService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error to search columns for general query.', error);
    }
  }

  async getColumnsForInvoiceConsultation() {
    try {
      if (this.invoiceHeader.length === 0) {
        this.invoiceHeader = await firstValueFrom(this.devolutionService.getColumnsForInvoiceConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de facturas.', error);
    }
  }

  async getDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.devolutionDetailService.getAllDevolutionDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los detalles de las devoluciones.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.devolutionService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de las devoluciones.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error to get permission profile by URL.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.Devolution));
  }

  async getDevolutionsWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.devolutionService.getAllDevolutionsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar las devoluciones.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.invoiceHeader.length = this.invoiceHeader.length === 1 ? 0 : this.invoiceHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getDevolutionsWithPagination(),
          this.getIndicatorsForGeneralConsultation()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailsConsultation(),
          this.getDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForInvoiceConsultation(),
          this.getAllInvoiceForDevolutionsWithPagination()
        ]);
        this.invoiceData = {
          ...this.invoiceData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) { this.router.navigate([`/devolution/register/${event.item.id}`]); }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailsConsultation();
        break;
      case 2:
        await this.exportInvoiceForDevolutionConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getDevolutionsWithPagination()
        break;
      case 1:
        await this.getDetailsWithPagination()
        break;
      case 2:
        await this.getAllInvoiceForDevolutionsWithPagination();
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}

